import { PageShell, PageSkeleton } from "@/components/Layout"
import { useRouteBreadcrumbs } from "@/navigation/route-meta"
import { Text } from "@chakra-ui/react"
import { useQuery } from "@tanstack/react-query"
import { createFileRoute, useNavigate } from "@tanstack/react-router"
import { useMemo } from "react"
import { z } from "zod"
import { QuestionsService, QuizzesService } from "../../client"
import QuestionAnalyticsDashboard from "../../components/Questions/QuestionAnalytics/QuestionAnalyticsDashboard"

const questionAnalyticsSearchSchema = z.object({
  range: z.enum(["7d", "30d", "90d", "all"]).catch("30d"),
})

type QuestionAnalyticsSearchState = z.infer<
  typeof questionAnalyticsSearchSchema
>

export const Route = createFileRoute(
  "/_layout/questions/$questionId/analytics",
)({
  component: QuestionAnalytics,
  validateSearch: (search) => questionAnalyticsSearchSchema.parse(search),
})

function QuestionAnalytics() {
  const { questionId } = Route.useParams()
  const navigate = useNavigate({ from: Route.fullPath })
  const { range } = Route.useSearch()

  const analyticsQueryDates = useMemo(() => {
    const end = new Date()

    switch (range) {
      case "7d": {
        const start = new Date(end)
        start.setDate(start.getDate() - 7)
        return { startDate: start.toISOString(), endDate: undefined }
      }
      case "30d": {
        const start = new Date(end)
        start.setDate(start.getDate() - 30)
        return { startDate: start.toISOString(), endDate: undefined }
      }
      case "90d": {
        const start = new Date(end)
        start.setDate(start.getDate() - 90)
        return { startDate: start.toISOString(), endDate: undefined }
      }
      case "all":
        return { startDate: undefined, endDate: end.toISOString() }
    }
  }, [range])

  const {
    data: question,
    isLoading: isLoadingQuestion,
    error: questionError,
  } = useQuery({
    queryKey: ["question", questionId],
    queryFn: () => QuestionsService.getQuestion({ questionId }),
  })

  const { data: savedActivity, isLoading: isLoadingSavedActivity } = useQuery({
    queryKey: ["question-saved-analytics", questionId],
    queryFn: () => QuizzesService.getQuestionSavedAnalytics({ questionId }),
  })

  const {
    data: analytics,
    isLoading,
    error,
  } = useQuery({
    queryKey: ["question-analytics", questionId, range],
    queryFn: () =>
      QuestionsService.getQuestionAnalytics({
        questionId,
        startDate: analyticsQueryDates.startDate,
        endDate: analyticsQueryDates.endDate,
      }),
  })

  const breadcrumbs = useRouteBreadcrumbs("questions.index", [
    { label: "Edit Question", href: `/questions/${questionId}/edit` },
    { label: "Analytics", isCurrent: true },
  ])

  if (error) {
    return (
      <PageShell
        title="Question Analytics"
        breadcrumbs={breadcrumbs}
        description="Performance metrics and insights for this question."
      >
        <Text color="red.500">Error loading analytics: {error.message}</Text>
      </PageShell>
    )
  }

  if (questionError) {
    return (
      <PageShell
        title="Question Analytics"
        breadcrumbs={breadcrumbs}
        description="Performance metrics and insights for this question."
      >
        <Text color="red.500">
          Error loading question: {questionError.message}
        </Text>
      </PageShell>
    )
  }

  return (
    <PageShell
      title="Question Analytics"
      breadcrumbs={breadcrumbs}
      description="Performance metrics and insights for this question."
    >
      {isLoading || isLoadingQuestion || isLoadingSavedActivity ? (
        <PageSkeleton variant="detail" rows={4} />
      ) : analytics ? (
        <QuestionAnalyticsDashboard
          analytics={analytics}
          savedActivity={savedActivity}
          questionText={question?.question_text}
          activeDatePreset={range}
          onDatePresetChange={(nextRange) =>
            navigate({
              search: (prev: QuestionAnalyticsSearchState) => ({
                ...prev,
                range: nextRange,
              }),
            })
          }
        />
      ) : (
        <Text>No analytics data available</Text>
      )}
    </PageShell>
  )
}
